import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { chargersApi } from '@/lib/api';
import { useWebSocket } from '@/context/WebSocketContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OnlineIndicator } from '@/components/StatusBadge';
import { timeAgo } from '@/lib/utils';
import { Battery, Filter, RefreshCw, ChevronRight, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  Page, GlassCard, PageHeader, StatCard, SectionHeading, EmptyState, ErrorState, PageSkeleton,
  LoadingRows, SearchField, Field, ConfirmDialog, glassStrong, errorMessage
} from '@/components/glass/surfaces';

const ChargersPage = () => {
  const navigate = useNavigate();
  const { subscribe } = useWebSocket();
  const { can } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chargers, setChargers] = useState([]);
  const [filteredChargers, setFilteredChargers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reloadToken, setReloadToken] = useState(0);
  const [editCharger, setEditCharger] = useState(null);
  const [deleteCharger, setDeleteCharger] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchChargers = useCallback(async () => {
    try {
      const response = await chargersApi.getAll();
      setChargers(response.data || []);
      setError('');
    } catch (fetchError) {
      console.error('Failed to fetch chargers:', fetchError);
      setError(errorMessage(fetchError, 'Failed to load chargers'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChargers();
    const unsubscribe = subscribe('chargers', (message) => {
      if (['charger_online', 'charger_offline', 'charger_created', 'charger_deleted'].includes(message.type)) {
        fetchChargers();
      }
    });
    return () => unsubscribe();
  }, [subscribe, fetchChargers, reloadToken]);

  useEffect(() => {
    let filtered = chargers;
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((charger) =>
        (charger.charge_point_id || '').toLowerCase().includes(searchLower) ||
        (charger.vendor || '').toLowerCase().includes(searchLower) ||
        (charger.model || '').toLowerCase().includes(searchLower) ||
        (charger.site_id || '').toLowerCase().includes(searchLower)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((charger) => (statusFilter === 'online' ? charger.is_online : !charger.is_online));
    }
    setFilteredChargers(filtered);
  }, [chargers, search, statusFilter]);

  const openEditDialog = (charger) => {
    setEditCharger({
      charge_point_id: charger.charge_point_id,
      vendor: charger.vendor || '',
      model: charger.model || '',
      serial_number: charger.serial_number || '',
      firmware_version: charger.firmware_version || '',
      site_id: charger.site_id || ''
    });
  };

  const handleUpdateCharger = async (event) => {
    event.preventDefault();
    if (!editCharger) return;
    setSaving(true);
    try {
      const { charge_point_id, ...payload } = editCharger;
      await chargersApi.update(charge_point_id, payload);
      toast.success('Charger updated');
      setEditCharger(null);
      fetchChargers();
    } catch (updateError) {
      console.error('Failed to update charger:', updateError);
      toast.error(errorMessage(updateError, 'Failed to update charger'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCharger = async () => {
    if (!deleteCharger) return false;
    setSaving(true);
    try {
      await chargersApi.delete(deleteCharger.charge_point_id);
      toast.success('Charger deleted');
      setDeleteCharger(null);
      fetchChargers();
      return true;
    } catch (deleteError) {
      console.error('Failed to delete charger:', deleteError);
      toast.error(errorMessage(deleteError, 'Failed to delete charger'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onlineCount = chargers.filter((charger) => charger.is_online).length;
  const offlineCount = chargers.length - onlineCount;
  const isFiltered = Boolean(search) || statusFilter !== 'all';

  const renderActions = (charger) => (
    can('chargers.manage') ? (
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={(event) => {
            event.stopPropagation();
            openEditDialog(charger);
          }}
          aria-label={`Edit ${charger.charge_point_id}`}
          data-testid={`edit-charger-${charger.charge_point_id}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full text-destructive hover:text-destructive"
          onClick={(event) => {
            event.stopPropagation();
            setDeleteCharger(charger);
          }}
          disabled={charger.is_online}
          aria-label={charger.is_online ? `Disconnect ${charger.charge_point_id} before deleting it` : `Delete ${charger.charge_point_id}`}
          title={charger.is_online ? 'Disconnect the charger before deleting it' : 'Delete charger'}
          data-testid={`delete-charger-${charger.charge_point_id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ) : null
  );

  if (loading && chargers.length === 0 && !error) {
    return <PageSkeleton testId="chargers-loading" cards={3} />;
  }

  return (
    <Page testId="chargers-page">
      <PageHeader
        title="Chargers"
        description="Charge points connected to this network."
        actions={(
          <>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => { setLoading(chargers.length === 0); setReloadToken((value) => value + 1); }}
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button
              type="button"
              className="rounded-full"
              disabled={!can('requests.create')}
              title={can('requests.create') ? 'Create a charger request' : 'You do not have permission to create charger requests'}
              onClick={() => navigate('/charger-requests', { state: { openNewRequest: true } })}
              data-testid="add-charger-btn"
            >
              <Plus className="h-4 w-4" />
              New Charger Request
            </Button>
          </>
        )}
      />

      {error ? (
        <ErrorState
          title={chargers.length ? 'Charger list may be out of date' : 'Could not load chargers'}
          message={error}
          onRetry={() => { setLoading(chargers.length === 0); setReloadToken((value) => value + 1); }}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Total chargers" value={chargers.length} icon={Battery} tone="accent" />
        <StatCard label="Online" value={onlineCount} icon={Battery} tone="success" />
        <StatCard label="Offline" value={offlineCount} icon={Battery} tone="neutral" />
      </div>

      <GlassCard tone="subtle" className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <SearchField value={search} onChange={setSearch} placeholder="Search by ID, vendor, model, or site" testId="search-input" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full rounded-full bg-background/70 sm:w-44" data-testid="status-filter">
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Status" />
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </GlassCard>

      {error && chargers.length === 0 ? null : (
      <GlassCard className="p-5 sm:p-6">
        <SectionHeading
          title="All chargers"
          description={isFiltered ? `${filteredChargers.length} of ${chargers.length} shown` : `${chargers.length} registered`}
        />
        {loading ? (
          <LoadingRows />
        ) : filteredChargers.length === 0 ? (
          <div data-testid="no-chargers">
          <EmptyState
            icon={Battery}
            title={isFiltered ? 'No chargers match' : 'No chargers yet'}
            description={isFiltered
              ? 'Try a different search or status filter.'
              : 'Approved charger requests become charge points here once they are installed.'}
            action={isFiltered ? (
              <Button type="button" variant="outline" className="rounded-full" onClick={() => { setSearch(''); setStatusFilter('all'); }}>
                Clear filters
              </Button>
            ) : can('requests.view') ? (
              <Button type="button" className="rounded-full" onClick={() => navigate('/charger-requests')}>
                Go to charger requests
              </Button>
            ) : null}
          />
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <Table className="min-w-[820px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Charger ID</TableHead>
                    <TableHead className="whitespace-nowrap">Vendor / model</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="whitespace-nowrap">Last heartbeat</TableHead>
                    <TableHead>Firmware</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChargers.map((charger) => (
                    <TableRow
                      key={charger.charge_point_id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/chargers/${charger.charge_point_id}`)}
                      data-testid={`charger-row-${charger.charge_point_id}`}
                    >
                      <TableCell className="font-mono font-medium">{charger.charge_point_id}</TableCell>
                      <TableCell>
                        <p>{charger.vendor || '-'}</p>
                        <p className="text-xs text-muted-foreground">{charger.model || '-'}</p>
                      </TableCell>
                      <TableCell>
                        <OnlineIndicator isOnline={charger.is_online} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{timeAgo(charger.last_heartbeat)}</TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{charger.firmware_version || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {renderActions(charger)}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden" data-testid="chargers-mobile-list">
              {filteredChargers.map((charger) => (
                <div key={charger.charge_point_id} className="rounded-2xl bg-black/[0.03] p-4 dark:bg-white/[0.04]">
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 text-left"
                    onClick={() => navigate(`/chargers/${charger.charge_point_id}`)}
                  >
                    <div className="min-w-0">
                      <p className="break-all font-mono font-medium">{charger.charge_point_id}</p>
                      <p className="text-sm text-muted-foreground">{charger.vendor || '-'} {charger.model || ''}</p>
                    </div>
                    <OnlineIndicator isOnline={charger.is_online} />
                  </button>
                  <div className="mt-3 flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <span>{timeAgo(charger.last_heartbeat)}</span>
                    <span className="font-mono">{charger.firmware_version || '-'}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    {renderActions(charger)}
                    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => navigate(`/chargers/${charger.charge_point_id}`)}>
                      Open
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </GlassCard>
      )}

      <Dialog open={!!editCharger} onOpenChange={(open) => { if (!open && !saving) setEditCharger(null); }}>
        <DialogContent className={`${glassStrong} max-h-[85vh] max-w-xl overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Edit charger</DialogTitle>
            <DialogDescription>The charger ID stays the same. Other details can be updated.</DialogDescription>
          </DialogHeader>
          {editCharger ? (
            <form className="grid gap-4" onSubmit={handleUpdateCharger}>
              <Field label="Charger ID">
                <Input value={editCharger.charge_point_id} disabled className="rounded-xl font-mono" />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  ['vendor', 'Vendor'],
                  ['model', 'Model'],
                  ['serial_number', 'Serial number'],
                  ['firmware_version', 'Firmware version'],
                  ['site_id', 'Site ID']
                ].map(([field, label]) => (
                  <Field label={label} key={field}>
                    <Input
                      value={editCharger[field]}
                      onChange={(event) => setEditCharger({ ...editCharger, [field]: event.target.value })}
                      className="rounded-xl"
                      data-testid={`edit-charger-${field}`}
                    />
                  </Field>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditCharger(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full" disabled={saving} data-testid="save-edit-charger-btn">
                  {saving ? 'Saving...' : 'Save changes'}
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteCharger}
        title={`Delete ${deleteCharger?.charge_point_id || 'charger'}?`}
        description="This permanently deletes the charger and its connectors, sessions, meter values, alarms, and OCPP message history."
        confirmLabel={saving ? 'Deleting...' : 'Delete charger'}
        destructive
        loading={saving}
        onOpenChange={(open) => { if (!open) setDeleteCharger(null); }}
        onConfirm={handleDeleteCharger}
        testId="confirm-delete-charger-btn"
      />
    </Page>
  );
};

export default ChargersPage;
